import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, Clock, Building2, ShieldCheck } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { pathFor } from "../lib/paths";

export default function Footer() {
  const { lang, t, raw } = useI18n();
  const home = pathFor("home", lang);
  const legal = raw("footer.legal_info");
  const year = new Date().getFullYear();

  return (
    <footer
      data-testid="site-footer"
      className="border-t border-slate-800 bg-slate-950 text-slate-200"
    >
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-10">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Link
              to={home}
              data-testid="footer-logo"
              className="inline-flex items-center gap-2 text-white"
            >
              <span
                aria-hidden="true"
                className="flex h-9 w-9 items-center justify-center rounded-md bg-amber-600 text-slate-950"
              >
                <span
                  className="text-lg font-extrabold"
                  style={{ fontFamily: "'Cabinet Grotesk', 'IBM Plex Sans', sans-serif" }}
                >
                  D
                </span>
              </span>
              <span
                className="text-xl font-extrabold tracking-tight"
                style={{ fontFamily: "'Cabinet Grotesk', 'IBM Plex Sans', sans-serif" }}
              >
                Dervaniq
              </span>
            </Link>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-slate-400">
              {t("footer.tagline")}
            </p>
            <div className="mt-6 flex items-center gap-2 text-xs uppercase tracking-widest text-amber-500">
              <ShieldCheck className="h-4 w-4" aria-hidden="true" />
              {legal.qualiopi}
            </div>
          </div>

          <div className="lg:col-span-2">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-400">
              {t("footer.sections.company")}
            </h3>
            <ul className="mt-4 space-y-3 text-sm">
              <li>
                <a
                  href={`${home}#approach`}
                  data-testid="footer-link-about"
                  className="text-slate-300 hover:text-white"
                >
                  {t("footer.links.about")}
                </a>
              </li>
              <li>
                <a
                  href={`${home}#approach`}
                  data-testid="footer-link-approach"
                  className="text-slate-300 hover:text-white"
                >
                  {t("footer.links.approach")}
                </a>
              </li>
              <li>
                <a
                  href={`${home}#insights`}
                  data-testid="footer-link-insights"
                  className="text-slate-300 hover:text-white"
                >
                  {t("footer.links.insights")}
                </a>
              </li>
              <li>
                <a
                  href={`${home}#faq`}
                  data-testid="footer-link-faq"
                  className="text-slate-300 hover:text-white"
                >
                  {t("footer.links.faq")}
                </a>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-400">
              {t("footer.sections.legal")}
            </h3>
            <ul className="mt-4 space-y-3 text-sm">
              <li>
                <Link
                  to={pathFor("privacy", lang)}
                  data-testid="footer-privacy-link"
                  className="text-slate-300 hover:text-white"
                >
                  {t("footer.links.privacy")}
                </Link>
              </li>
              <li>
                <Link
                  to={pathFor("cookies", lang)}
                  data-testid="footer-cookies-link"
                  className="text-slate-300 hover:text-white"
                >
                  {t("footer.links.cookies")}
                </Link>
              </li>
              <li>
                <Link
                  to={pathFor("terms", lang)}
                  data-testid="footer-terms-link"
                  className="text-slate-300 hover:text-white"
                >
                  {t("footer.links.terms")}
                </Link>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-400">
              {t("footer.sections.contact")}
            </h3>
            <ul className="mt-4 space-y-3 text-sm">
              <li className="flex items-start gap-2">
                <Mail className="mt-0.5 h-4 w-4 text-amber-500" aria-hidden="true" />
                <a
                  href={`mailto:${raw("footer.contact.email")}`}
                  data-testid="footer-email-link"
                  className="text-slate-300 hover:text-white"
                >
                  {raw("footer.contact.email")}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Phone className="mt-0.5 h-4 w-4 text-amber-500" aria-hidden="true" />
                <a
                  href={`tel:${raw("footer.contact.phone").replace(/\s+/g, "")}`}
                  data-testid="footer-phone-link"
                  className="text-slate-300 hover:text-white"
                >
                  {raw("footer.contact.phone")}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Clock className="mt-0.5 h-4 w-4 text-amber-500" aria-hidden="true" />
                <span className="text-slate-300">{raw("footer.contact.hours")}</span>
              </li>
              <li className="flex items-start gap-2">
                <Building2 className="mt-0.5 h-4 w-4 text-amber-500" aria-hidden="true" />
                <span className="text-slate-300">{legal.address}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 grid gap-3 border-t border-slate-800 pt-8 text-xs text-slate-400 md:grid-cols-2">
          <div className="space-y-1">
            <p>{legal.company_name} — {legal.legal_form}</p>
            <p>{legal.siret} · {legal.rcs}</p>
            <p>{legal.tva} · {legal.ape}</p>
            <p>{legal.director}</p>
            <p>{legal.dpo}</p>
          </div>
          <div className="space-y-1 md:text-right">
            <p>{legal.hosting}</p>
            <p>{legal.insurance}</p>
            <p>{legal.incorporation} · {legal.jurisdiction}</p>
          </div>
        </div>

        <p className="mt-8 text-xs text-slate-500">
          © {year} Dervaniq SAS. {t("footer.rights")}
        </p>
      </div>
    </footer>
  );
}
